import React, { useEffect, useState } from "react";
import {
    Grid,
    TextField,
    FormLabel,
    IconButton,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Typography,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import DeleteIcon from "@mui/icons-material/Delete";
import { AddCircleOutline, RemoveCircleOutlineOutlined } from "@mui/icons-material";

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const QueueSelector = ({ availableQueue = [], pccList, setPccList }) => {
    const [confirmRemovePCCOpen, setConfirmRemovePCCOpen] = useState(false);
    const [pccToRemove, setPccToRemove] = useState(null);

    const confirmRemovePCC = (pccValue) => {
        setPccToRemove(pccValue);
        setConfirmRemovePCCOpen(true);
    };

    const handleConfirmRemovePCC = () => {
        if (pccToRemove) {
            setPccList((prev) => prev.filter((p) => p.pcc !== pccToRemove));
        }
        setPccToRemove(null);
        setConfirmRemovePCCOpen(false);
    };

    useEffect(() => {
        if (!Array.isArray(availableQueue) || availableQueue.length === 0) return;

        setPccList((prev) =>
            prev.map((pcc) => {
                let queues = pcc.queues || [];
                const hasQ22 = queues.some((q) => q.queue === "Q22");

                if (!hasQ22) {
                    queues = [
                        { queue: "Q22", email: "", ccEmail: "", errors: {} },
                        ...queues,
                    ];
                }

                return { ...pcc, queues };
            })
        );
    }, [availableQueue]);

    const handleAddQueue = (pccValue) => {
        setPccList((prev) =>
            prev.map((p) =>
                p.pcc === pccValue
                    ? {
                          ...p,
                          queues: [
                              ...p.queues,
                              { queue: "", email: "", ccEmail: "", errors: {} },
                          ],
                      }
                    : p
            )
        );
    };

    const validateEmailFields = (queueObj) => {
        const errors = {};

        // Email required + pattern check
        if (!queueObj.email.trim()) {
            errors.email = "Default Email is required.";
        } else if (!emailRegex.test(queueObj.email.trim())) {
            errors.email = "Invalid email format.";
        }

        // CC email optional but validate format
        if (queueObj.ccEmail.trim() && !emailRegex.test(queueObj.ccEmail.trim())) {
            errors.ccEmail = "Invalid CC email format.";
        }

        return errors;
    };

    const handleSelectQueue = (pccValue, index, newQueue) => {
        if (!newQueue) return;

        setPccList((prev) =>
            prev.map((p) =>
                p.pcc === pccValue
                    ? {
                          ...p,
                          queues: p.queues.map((q, i) =>
                              i === index
                                  ? {
                                        ...q,
                                        queue: newQueue,
                                    }
                                  : q
                          ),
                      }
                    : p
            )
        );
    };

    const handleQueueEmailChange = (pccValue, index, newValue, isCC = false) => {
        setPccList((prev) =>
            prev.map((p) =>
                p.pcc === pccValue
                    ? {
                          ...p,
                          queues: p.queues.map((q, i) => {
                              if (i !== index) return q;

                              const updated = {
                                  ...q,
                                  [isCC ? "ccEmail" : "email"]: newValue,
                              };

                              updated.errors = validateEmailFields(updated);

                              return updated;
                          }),
                      }
                    : p
            )
        );
    };

    const handleRemoveQueue = (pccValue, index) => {
        setPccList((prev) =>
            prev.map((p) =>
                p.pcc === pccValue
                    ? {
                          ...p,
                          queues: p.queues.filter(
                              (q, i) => !(i === index && q.queue !== "Q22")
                          ),
                      }
                    : p
            )
        );
    };

    return (
        <Grid container spacing={2} sx={{ p: 2, borderRadius: 2 }}>
            {pccList.map((pccData, index) => {
                const pccQueueData = availableQueue.find(
                    (item) => item.pcc === pccData.pcc
                );
                const allQueues = pccQueueData?.queues || [];

                return (
                    <Grid
                        key={pccData.pcc}
                        item
                        xs={12}
                        sx={{
                            border: "1px solid #ccc",
                            p: 1,
                            mt: index === 0 ? 0 : 2,
                            borderRadius: 2,
                            position: "relative",
                        }}
                    >
                        <IconButton
                            color="error"
                            size="small"
                            onClick={() => confirmRemovePCC(pccData.pcc)}
                            sx={{ position: "absolute", top: 8, right: 8 }}
                        >
                            <RemoveCircleOutlineOutlined fontSize="small" />
                        </IconButton>

                        <Grid container spacing={1} md={12}>
                            <Grid item xs={12} md={1.5} sx={{ display: "flex", alignItems: "center" }}>
                                <FormLabel sx={{ fontWeight: "bold", fontSize: "1.1rem" }}>
                                    PCC: {pccData.pcc}
                                </FormLabel>
                            </Grid>

                            <Grid item xs={12} md={10}>
                                {pccData.queues.map((queueObj, i) => {
                                    const selectedQueues = pccData.queues.map((q) => q.queue);

                                    const availableOptions = allQueues.filter(
                                        (q) =>
                                            !selectedQueues.includes(q) || q === queueObj.queue
                                    );

                                    const isAllQueuesAdded = selectedQueues.length === allQueues.length;

                                    return (
                                        <Grid
                                            key={i}
                                            container
                                            alignItems="center"
                                            spacing={1}
                                            sx={{ mt: i === 0 ? 0 : 1 }}
                                        >
                                            <Grid item xs={12} md={2}>
                                                <Autocomplete
                                                    fullWidth
                                                    options={availableOptions}
                                                    value={queueObj.queue || ""}
                                                    onChange={(e, newValue) =>
                                                        handleSelectQueue(pccData.pcc, i, newValue)
                                                    }
                                                    renderInput={(params) => (
                                                        <TextField {...params} label="Queue" />
                                                    )}
                                                />
                                            </Grid>

                                            {/* EMAIL FIELD WITH VALIDATION */}
                                            <Grid item xs={12} md={4.5}>
                                                <TextField
                                                    fullWidth
                                                    label="Email"
                                                    value={queueObj.email || ""}
                                                    onChange={(e) =>
                                                        handleQueueEmailChange(
                                                            pccData.pcc,
                                                            i,
                                                            e.target.value
                                                        )
                                                    }
                                                    error={!!queueObj.errors?.email}
                                                    helperText={queueObj.errors?.email || ""}
                                                />
                                            </Grid>

                                            {/* CC EMAIL FIELD WITH VALIDATION */}
                                            <Grid item xs={12} md={4.5}>
                                                <TextField
                                                    fullWidth
                                                    label="CC Email"
                                                    value={queueObj.ccEmail || ""}
                                                    onChange={(e) =>
                                                        handleQueueEmailChange(
                                                            pccData.pcc,
                                                            i,
                                                            e.target.value,
                                                            true
                                                        )
                                                    }
                                                    error={!!queueObj.errors?.ccEmail}
                                                    helperText={queueObj.errors?.ccEmail || ""}
                                                />
                                            </Grid>

                                            {queueObj.queue === "Q22" && (
                                                <Grid item xs={12} md={1}>
                                                    <IconButton
                                                        color="primary"
                                                        onClick={() => handleAddQueue(pccData.pcc)}
                                                        disabled={isAllQueuesAdded}
                                                    >
                                                        <AddCircleOutline fontSize="medium" />
                                                    </IconButton>
                                                </Grid>
                                            )}

                                            <Grid item xs={12} md={1}>
                                                {queueObj.queue !== "Q22" && (
                                                    <IconButton
                                                        color="error"
                                                        onClick={() => handleRemoveQueue(pccData.pcc, i)}
                                                    >
                                                        <DeleteIcon />
                                                    </IconButton>
                                                )}
                                            </Grid>
                                        </Grid>
                                    );
                                })}
                            </Grid>
                        </Grid>
                    </Grid>
                );
            })}

            {/* CONFIRM REMOVE PCC DIALOG */}
            <Dialog
                open={confirmRemovePCCOpen}
                onClose={() => setConfirmRemovePCCOpen(false)}
            >
                <DialogTitle>Remove PCC</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to remove{" "}
                        <strong>{pccToRemove}</strong>?
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmRemovePCCOpen(false)}>No</Button>
                    <Button variant="contained" color="error" onClick={handleConfirmRemovePCC}>
                        Yes, Remove
                    </Button>
                </DialogActions>
            </Dialog>
        </Grid>
    );
};

export default QueueSelector;
